export interface ChronologyDay {
  day: number;
  count: number;
}

export interface ChronologyMonth {
  month: number;
  count: number;
  days: ChronologyDay[];
}

export interface ChronologyYear {
  year: number;
  count: number;
  months: ChronologyMonth[];
}

export interface ChronologyTree {
  totalCount: number;
  years: ChronologyYear[];
}

export interface Tag {
  id: string;
  name: string;
  color: string;
  usageCount?: number;
  searchText?: string | null;
  groupId?: string;
  groupName?: string;
  groupPosition: number;
  position: number;
}

export interface TagGroup {
  id: string;
  name: string;
  position: number;
}

export interface DocumentFile {
  id: string;
  documentId: string;
  fileName: string;
  contentType: string;
  uploadedAt: string;
  hasPreview: boolean;
  position: number;
  hasEmbeddedText: boolean;
  fileSizeBytes?: number | null;
  fileHash?: string | null;
  pageCount?: number | null;
  isDuplicate?: boolean;
  duplicateSkipped?: boolean;
  duplicateDocumentId?: string | null;
  duplicateDocumentTitle?: string | null;
  duplicateFileName?: string | null;
  contentClassification?: string | null;
}

export interface CamtMonthlyPoint {
  month: string;
  balance: number;
  hasBalance: boolean;
  incoming: number;
  outgoing: number;
  netChange: number;
  transactionCount: number;
  sourceDocumentId?: string;
  sourceFileId?: string;
  sourceFileName?: string;
}

export interface CamtAnalyticsResponse {
  iban: string;
  currency: string;
  sourceFileCount: number;
  transactionCount: number;
  months: CamtMonthlyPoint[];
}

export interface PdfMarkerRectangle {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface PdfMarker {
  id: string;
  documentId: string;
  documentFileId: string;
  pageNumber: number;
  selectedText: string;
  color: string;
  comment: string;
  sourceFileHash: string;
  rectangles: PdfMarkerRectangle[];
  creator: string;
  lastUpdater: string;
  createdAt: string;
  lastUpdatedAt: string;
}

export interface PdfBookmark {
  id: string;
  documentId: string;
  documentFileId: string;
  documentTitle: string;
  fileName: string;
  pageNumber: number;
  selectedText: string;
  rectangles: PdfMarkerRectangle[];
  title: string;
  description: string;
  sourceFileHash: string;
  tags: Tag[];
  creator: string;
  lastUpdater: string;
  createdAt: string;
  lastUpdatedAt: string;
}

export interface PdfDocumentLink {
  id: string;
  documentId: string;
  documentFileId: string;
  pageNumber: number;
  selectedText: string;
  rectangles: PdfMarkerRectangle[];
  sourceFileHash: string;
  targetDocumentId: string;
  targetDocumentTitle: string;
  creator: string;
  lastUpdater: string;
  createdAt: string;
  lastUpdatedAt: string;
}

export interface FollowUp {
  id: string;
  documentId: string;
  dueDate: string;
  note?: string | null;
  forUser?: string | null;
  forUserDisplayName?: string | null;
  creatorUsername?: string | null;
  creatorDisplayName?: string | null;
  createdAt?: string | null;
}

export type FollowUpHistoryAction = 'created' | 'updated' | 'completed';

export interface FollowUpHistoryAvailability {
  count: number;
  hasHistory: boolean;
}

export interface FollowUpHistoryEntry {
  id: string;
  documentId: string;
  followUpId: string;
  action: FollowUpHistoryAction | string;
  occurredAt: string;
  dueDate: string;
  note?: string | null;
  forUserId: number;
  forUsername?: string | null;
  forDisplayName?: string | null;
  followUpCreator: string;
  followUpCreatorDisplayName?: string | null;
  followUpLastUpdater: string;
  followUpLastUpdaterDisplayName?: string | null;
  followUpCreatedAt: string;
  followUpLastUpdatedAt: string;
  changedBy: string;
  changedByDisplayName?: string | null;
}

export interface SharedUser {
  username: string;
  displayName?: string | null;
}

export interface FollowUpItem {
  documentId: string;
  documentTitle: string;
  followUpId: string;
  dueDate: string;
  note?: string | null;
  forUser?: string | null;
  forUserDisplayName?: string | null;
  creatorDisplayName?: string | null;
  group: 'overdue' | 'today' | 'tomorrow' | 'upcoming';
}

export type SecurityLevel = 0 | 1 | 2 | 3;
export const SECURITY_LEVELS: { value: SecurityLevel; key: string }[] = [
  { value: 0, key: 'documents.securityLevel.public' },
  { value: 1, key: 'documents.securityLevel.internal' },
  { value: 2, key: 'documents.securityLevel.confidential' },
  { value: 3, key: 'documents.securityLevel.restricted' },
];

export interface Document {
  id: string;
  title: string;
  description: string;
  documentDate: string;
  createdAt: string;
  lastUpdatedAt?: string | null;
  creator?: string | null;
  creatorDisplayName?: string | null;
  lastUpdater?: string | null;
  lastUpdaterDisplayName?: string | null;
  owner?: string | null;
  ownerDisplayName?: string | null;
  incoming: boolean;
  isFormOnly: boolean;
  securityLevel: SecurityLevel;
  files: DocumentFile[];
  tags: Tag[];
  followUps: FollowUp[];
  sharedWith: SharedUser[];
}

export interface DocumentListResponse {
  documents: Document[];
  totalCount: number;
}

export interface ImportProtocolEntry {
  id: string;
  protocolNumber: number;
  originalFileName: string;
  startedAt: string;
  completedAt?: string | null;
  documentFileId?: string | null;
  sourceDocumentId?: string | null;
  sourceDocumentTitle?: string | null;
  currentDocumentId?: string | null;
  currentDocumentTitle?: string | null;
  targetDocumentId?: string | null;
  targetDocumentTitle?: string | null;
  appliedRuleTitle?: string | null;
  outcome: string;
  isDuplicate: boolean;
  error?: string | null;
}

export interface ImportProtocol {
  number: number;
  startedAt: string;
  completedAt?: string | null;
  owner?: string | null;
  ownerDisplayName?: string | null;
  entries: ImportProtocolEntry[];
}

export interface CreateDocumentDto {
  title: string;
  description: string;
  documentDate: string;
  tagNames?: string[];
  securityLevel?: number;
}

export interface UpdateDocumentDto {
  title: string;
  description: string;
  documentDate: string;
  tagNames?: string[];
  securityLevel?: number;
}

export interface UserSettingEntry {
  key: string;
  value: string;
}

export interface Pop3EmailAccountSettings {
  id: number;
  pop3Server: string;
  port: number;
  useSsl: boolean;
  useStartTls: boolean;
  username: string;
  hasPassword: boolean;
  checkIntervalMinutes: number;
  enabled: boolean;
  connectionTimeoutSeconds: number;
  lastError?: string | null;
  lastCheckedAt?: string | null;
  lastSuccessfulCheckAt?: string | null;
  configured: boolean;
}

export interface Pop3EmailAccountResponse {
  configured: boolean;
  settings: Pop3EmailAccountSettings;
}

export interface Pop3EmailConnectionTestResponse {
  success: boolean;
  error?: string | null;
}

export interface Pop3EmailRetrieveResponse {
  success: boolean;
  importedCount: number;
  failedCount: number;
  error?: string | null;
}

export interface MeResponse {
  username: string;
  displayName?: string;
  permissions: string[];
}

export interface FileSizeStats {
  totalFilesSizeBytes: number;
  totalPreviewSizeBytes: number;
  ownFilesSizeBytes: number;
  otherFilesSizeBytes: number;
  avgPdfSizeBytes: number;
  avgImageSizeBytes: number;
  totalPageCount: number;
  avgPdfPageCount: number;
}

export interface StatisticsResponse {
  ownCount: number;
  sharers: { displayName: string; count: number }[];
  otherSharerCount: number;
  realDocumentCount: number;
  formOnlyDocumentCount: number;
  documentDateHistogram: { year: number; count: number; realCount: number; formOnlyCount: number }[];
  growth: { label: string; cumulative: number; realCumulative: number; formOnlyCumulative: number }[];
  fileSizeStats: FileSizeStats;
  fileSizeGrowth: { label: string; filesCumulativeBytes: number; previewsCumulativeBytes: number }[];
}

export interface Rule {
  id: string;
  title: string;
  selector: string;
  code: string;
  creator: string;
  lastUpdater: string;
  createdAt: string;
  updatedAt: string;
}

export type FormFieldType = 'text' | 'number' | 'date' | 'boolean' | 'tagSelect';

export interface FormFieldDefinition {
  id: string;
  name: string;
  dataType: FormFieldType;
  tagGroupId?: string | null;
  addTagToDocument: boolean;
  uniqueForDocument: boolean;
  position: number;
}

export interface FormDefinition {
  id: string;
  title: string;
  isActive: boolean;
  fields: FormFieldDefinition[];
  creator: string;
  lastUpdater: string;
  createdAt: string;
  updatedAt: string;
}

export interface FormViewDefinition {
  id: string;
  title: string;
  viewType: string;
  definitionJson: string;
  creator: string;
  lastUpdater: string;
  createdAt: string;
  updatedAt: string;
}

export interface FormViewDataPoint {
  documentDate: string;
  value: number;
  isFormDate: boolean;
  documentId?: string | null;
}

export interface FormViewSeries {
  name: string;
  points: FormViewDataPoint[];
  color?: string | null;
  metricId: string;
  unit?: string | null;
}

export interface FormViewData {
  viewType: string;
  chartType: 'line' | 'bar';
  yAxisLabel?: string | null;
  pointMarkers: 'all' | 'formDates' | 'none';
  series: FormViewSeries[];
}

export interface RuleResult {
  documentDate?: string | null;
  title?: string | null;
  tags?: string[] | null;
  securityLevel?: number | null;
}

export interface RuleApplicationResponse {
  applied: boolean;
  matched: boolean;
  changed: boolean;
  documentTitle: string;
  targetDocumentId?: string | null;
  fileMoved?: boolean;
  sourceDocumentDeleted?: boolean;
  duplicateSkipped?: boolean;
  appliedRuleTitle?: string | null;
  error?: string | null;
}

export interface UploadConfig {
  id: string;
  name: string;
  selector: string;
  checkIntervalMs: number;
  stabilityMs: number;
  timeoutMs: number;
}

export interface MaintenanceInfo {
  filesWithoutSize: number;
  emptyFolderCount: number;
  pdfFilesWithoutOcr: number;
  pdfFilesWithoutText: number;
  filesWithoutHash: number;
  filesWithoutPageCount: number;
}

export interface RestoreResetChallenge {
  challengeId: string;
  confirmationCode: string;
  expiresInSeconds: number;
}

export interface RestoreResetResult {
  deletedDocumentCount: number;
  deletedFileCount: number;
  deletedStorageEntryCount: number;
}

export interface MaintenanceProgressEvent {
  processed: number;
  total: number;
  message: string;
  done: boolean;
  error: boolean;
  updatedCount: number;
  skippedCount: number;
  errorCount: number;
  deletedCount: number;
}

export interface ArchiveExportJobStatus {
  jobId: string;
  state: 'queued' | 'building' | 'ready' | 'failed';
  phase: string;
  processed: number;
  total: number;
  fileName: string;
  error?: string | null;
}

export interface BackupJobPartStatus {
  partNumber: number;
  fileName: string;
  length: number;
}

export interface BackupJobStatus {
  jobId: string;
  state: 'queued' | 'building' | 'ready' | 'failed';
  backupId: string;
  processedParts: number;
  partCount: number;
  processedBytes: number;
  parts: BackupJobPartStatus[];
  error?: string | null;
}

export interface BackupCapabilities {
  desktopMode: boolean;
  currentUserId: number;
  maxPartBytes: number;
}

export interface BackupUser {
  id: number;
  sub: string;
  username: string;
  email: string;
  displayName: string;
  createdAt: string;
  updatedAt: string;
}

export interface BackupInspection {
  backupId: string;
  schemaVersion: number;
  createdAt: string;
  sourceDatabaseProvider: string;
  partCount: number;
  documentCount: number;
  fileCount: number;
  totalFileBytes: number;
  desktopMode: boolean;
  users: BackupUser[];
  targetUsers: BackupUser[];
}

export interface BackupUserMapping {
  sourceUserId: number;
  targetUserId: number;
  targetSub: string;
  targetUsername: string;
  targetEmail: string;
  targetDisplayName: string;
}

export interface BackupRestoreResult {
  backupId: string;
  userCount: number;
  documentCount: number;
  fileCount: number;
  totalFileBytes: number;
  warnings: string[];
}
